// Шапка карточки подотчётного лица: остаток крупно, три корзины и сверка инварианта.
//
// Три корзины отдельными числами, а не одной суммой: заказчик просил видеть и то, за что
// отчитались, и то, что ещё не проверено, и то, что отклонено. Свести их в одно значит
// вернуть его к сегодняшнему состоянию, где непонятно, где деньги.
//
// Сверка инварианта показывается ВСЕГДА, а не только при расхождении. «Сходится» — это тоже
// сведение, и человек должен его видеть: молчание читается как «проверка не делалась».
//
// ## Почему остаток и корзины стоят друг под другом, а не по краям
//
// До 25.08.2026 корзины прижимались к правому краю через `justify-between`. На окне в 1600
// точек это давало 600 точек пустоты между остатком и корзинами — числа переставали читаться
// как разложение одного и того же и выглядели двумя не связанными группами. Теперь они идут
// под остатком одним рядом с разделителями: сверху сумма, под ней — из чего она состоит.
//
// Отступ 20 точек, а не 24: ровно столько же у шапки детали выше. При 24 крупное число
// начиналось на четыре точки правее имени человека — на глаз это и есть «съехало».

import type { PersonCardOut } from '@/shared/api/endpoints/accountable';
import { formatKopecks } from '@/shared/money/format';
import * as Alert from '@/shared/ui/alert';
import { Metric, MetricRow } from '@/shared/ui/metric';

import { LEVEL_TITLES } from '../dictionaries';

export interface BalanceHeaderProps {
  person: PersonCardOut;
}

export function BalanceHeader({ person }: BalanceHeaderProps) {
  const { balance } = person;
  return (
    <div className='flex flex-col gap-5 border-b border-stroke-soft-200 bg-bg-white-0 p-5'>
      <div className='flex flex-col gap-1'>
        <Metric
          tone='главный'
          label={`Неподтверждённый остаток · ${LEVEL_TITLES[person.level] ?? person.level}`}
          value={formatKopecks(balance.unconfirmed_balance)}
        />
        {/* Пояснение движка показывается ТОЛЬКО когда числа сами себя не объясняют.
            Обычный случай движок описывает фразой «На вас числится 13 311 851,00. На
            рассмотрении 0,00, не отчитано 13 311 851,00» — а ровно эти три числа стоят
            подписанными величинами прямо под ней. Один и тот же факт дважды: человек читает
            фразу, потом те же числа, и ищет между ними разницу.
            Случай «компания должна» — другой: там отрицательный остаток, и без слов он
            читается как ошибка расчёта, а не как «вы потратили своих». */}
        {balance.company_owes && (
          <span className='max-w-prose text-paragraph-sm text-text-sub-600'>
            {balance.explanation}
          </span>
        )}
      </div>

      {/* Ширина не ограничивается: ряд величин с разделителями ведёт себя одинаково во всех
          блоках карточки. Ограничение оставлено только тексту-пояснению выше — там оно про
          читаемость строки, а не про числа. */}
      <MetricRow columns={3}>
        <Metric label='На рассмотрении' value={formatKopecks(balance.under_review)} />
        <Metric label='Не отчитано' value={formatKopecks(balance.not_reported)} />
        <Metric
          label='Отклонено · справочно'
          value={formatKopecks(balance.rejected)}
          tone='приглушённый'
        />
      </MetricRow>

      {balance.in_transit_out > 0 && (
        <Alert.Root variant='lighter' status='information' size='small'>
          <Alert.Icon />
          {formatKopecks(balance.in_transit_out)} передаётся другому лицу, получатель ещё не
          подтвердил. У вас сумма пока не списана, у него не зачислена.
        </Alert.Root>
      )}
      {balance.in_transit_in > 0 && (
        <Alert.Root variant='lighter' status='information' size='small'>
          <Alert.Icon />
          Вам передают {formatKopecks(balance.in_transit_in)}. Пока вы не подтвердили получение,
          сумма не числится ни на вас, ни на передающем.
        </Alert.Root>
      )}

      {/* Расхождение инварианта — событие с карточкой, а не строка в отчёте: именно тихой
          строкой у заказчика и потерялись 51 000 ₽. */}
      {person.invariant_ok ? (
        <span className='text-paragraph-xs text-text-sub-600'>{person.invariant_text}</span>
      ) : (
        <Alert.Root variant='lighter' status='warning' size='small'>
          <Alert.Icon />
          {person.invariant_text}
        </Alert.Root>
      )}
    </div>
  );
}
